import { useKeyboardShortcuts, type KeyboardShortcut } from '@/util/use-keyboard-shortcuts';
import { useEffect, useMemo, useRef, type PropsWithChildren } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { ArrowLeftCircle, Loader } from 'react-feather';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card } from './card';
import { useTranslation } from 'react-i18next';

type ButtonVariant = 'primary' | 'secondary' | 'warning' | 'danger' | 'success';

type ButtonHotkey = Omit<KeyboardShortcut, 'action'> & {
    display: React.ReactNode;
};

type PrimaryProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    children: React.ReactNode;
    variant?: ButtonVariant;
    hotkey?: ButtonHotkey;
    isLoading?: boolean;
};

export function Button({ children, variant = 'primary', className = '', ...props }: PrimaryProps) {
    const colors: Record<ButtonVariant, string> = {
        primary: 'after:bg-primary text-foreground outline-secondary',
        secondary: 'after:bg-muted text-foreground outline-foreground',
        success: 'after:bg-success text-foreground outline-foreground',
        warning: 'after:bg-warning text-foreground outline-foreground',
        danger: 'after:bg-danger text-foreground outline-foreground',
        // secondaryDARK: 'text-background after:bg-secondary outline-foreground'
    };

    const color = colors[variant];
    const disabled = props.disabled || props.isLoading;
    const buttonRef = useRef<HTMLButtonElement>(null);

    const shortcuts = useMemo(() => props.hotkey ? [{
        key: props.hotkey.key,
        shift: props.hotkey.shift,
        ctrl: props.hotkey.ctrl,
        alt: props.hotkey.alt,
        meta: props.hotkey.meta,
        allowInInput: props.hotkey.allowInInput,
        action: () => buttonRef.current?.click(),
    }] : [], [props.hotkey]);

    useKeyboardShortcuts(shortcuts);

    useEffect(() => {
        if (props.autoFocus && !disabled) {
            buttonRef.current?.focus();
        }
    }, [props.autoFocus, disabled]);

    return (
        <button ref={buttonRef} {...props} autoFocus={props.autoFocus} disabled={disabled} className={`relative items-center justify-center rounded-xs after:rounded-xs px-4 py-2 font-thin outline z-10 select-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none after:absolute after:inset-0 after:translate-x-1 after:translate-y-1 after:transition-all after:duration-150 after:-z-10 active:after:translate-x-0 active:after:translate-y-0 transition-all duration-150 ${color} ${className}`}>
            {!!props.isLoading && <Loader className="inline w-4 h-4 animate-spin" />}
            {!props.isLoading && (
                <>
                    {children}
                    {props.hotkey && <span className="absolute pl-2 text-foreground opacity-50">{props.hotkey.display}</span>}
                </>
            )}
        </button>
    );
}

type ImageChoiceButton = ButtonHTMLAttributes<HTMLButtonElement> & {
    hotkey?: ButtonHotkey;
};

export function ImageChoiceButton({ children, ...props }: PropsWithChildren<ImageChoiceButton>) {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const shortcuts = useMemo(() => props.hotkey ? [{
        key: props.hotkey.key,
        shift: props.hotkey.shift,
        ctrl: props.hotkey.ctrl,
        alt: props.hotkey.alt,
        meta: props.hotkey.meta,
        allowInInput: props.hotkey.allowInInput,
        action: () => buttonRef.current?.click(),
    }] : [], [props.hotkey]);

    useKeyboardShortcuts(shortcuts);

    useEffect(() => {
        if (props.autoFocus && !props.disabled) {
            buttonRef.current?.focus();
        }
    }, [props.autoFocus, props.disabled]);

    return (
        <button ref={buttonRef} {...props} className={`relative block bg-muted/25 outline outline-secondary/50 w-full cursor-pointer ring-game-highlight hover:ring-4 rounded ${props.className}`}>
            {children}
            {props.hotkey && <span className="absolute bottom-0 right-0 px-2 text-foreground opacity-50">{props.hotkey.display}</span>}
        </button>
    );
}

export function ReturnButton() {
    const location = useLocation();
    const navigate = useNavigate();

    const shortcuts = useMemo(() => [
        { key: 'Escape', action: () => navigate('/'), allowInInput: true },
    ], [navigate]);

    useKeyboardShortcuts(shortcuts);

    const hiddenRoutes = [
        '/',
    ];

    return (
        <Link to="/" className={`absolute top-0 left-0 ${hiddenRoutes.includes(location.pathname) ? 'opacity-0' : 'opacity-60 hover:opacity-100'} bg-primary rounded-full z-10 focus:ring-3 ring-secondary/75`}>
            <ArrowLeftCircle className="w-8 h-8 text-foreground" />
        </Link>
    );
}

interface ShowEndGameButtonProps {
    onClick: () => void;
}

export function ShowEndGameButton(props: ShowEndGameButtonProps) {
    const { t } = useTranslation();

    return (
        <div className="fixed left-0 bottom-0 w-screen flex justify-center mb-10">
            <Card className="h-full w-lg max-w-2xl shadow-lg p-4 md:p-6">
                <Button hotkey={{ key: 'Escape', display: 'Esc' }} className="w-full" onClick={props.onClick}>
                    {t('game.gameplay.endGame.viewResults')}
                </Button>
            </Card>
        </div>
    );
}